import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Send } from "lucide-react";
import { scrollToId } from "@/lib/api";

const SmallKite = ({ className, style, color = "#cf4b26" }) => (
  <svg viewBox="0 0 40 52" className={className} style={style} fill="none" aria-hidden="true">
    <path d="M20 2 L34 18 L20 34 L6 18 Z" fill={color} stroke="#14355c" strokeWidth="1.6" />
    <path d="M20 2 L20 34 M6 18 L34 18" stroke="#14355c" strokeWidth="1.3" />
    <path d="M20 34 C 16 40, 24 44, 19 50" stroke="#14355c" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const Hero = ({ content }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const kiteY = useTransform(scrollYProgress, [0, 1], ["0%", "-45%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-[94vh] overflow-hidden flex flex-col"
      data-testid="hero-section"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-[1.08]">
        <img
          src={content.hero_image}
          alt="Folk art of a dancer flying kites over Odisha"
          className="w-full h-full object-cover"
          data-testid="hero-image"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-paper/50 via-transparent to-paper/30" />
      </motion.div>

      <motion.div style={{ y: kiteY }} className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <SmallKite className="absolute left-[6%] top-[16%] w-10 animate-kite-bob" style={{ "--r": "-12deg" }} />
        <SmallKite
          className="absolute right-[8%] top-[12%] w-12 animate-kite-bob"
          style={{ "--r": "10deg", animationDelay: "1.2s" }}
          color="#f0b52f"
        />
        <SmallKite
          className="hidden md:block absolute left-[16%] top-[36%] w-8 animate-kite-bob"
          style={{ "--r": "6deg", animationDelay: "0.6s" }}
          color="#2e7f86"
        />
      </motion.div>

      <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-5 pt-28 pb-16">
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-[6%] bottom-[2%] -z-10 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 62% 60% at 50% 46%, rgba(247,239,220,0.94) 0%, rgba(247,239,220,0.78) 46%, rgba(247,239,220,0.35) 66%, rgba(247,239,220,0) 82%)",
          }}
        />
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-body font-bold tracking-[0.45em] text-navy text-sm sm:text-base uppercase hero-type"
          data-testid="hero-kicker"
        >
          {content.hero_kicker}
        </motion.p>

        <h1 className="mt-4 font-display font-bold text-navy leading-[0.95] hero-type" data-testid="hero-title">
          {content.hero_title.split(" ").map((word, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span
                className="block text-[19vw] sm:text-8xl lg:text-9xl"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                {word}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.75 }}
          className="mt-3 font-script text-4xl sm:text-5xl text-verm hero-type"
          data-testid="hero-tagline"
        >
          {content.tagline}
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-5 max-w-xl font-body font-medium text-navy/85 text-base sm:text-lg leading-relaxed hero-type"
          data-testid="hero-text"
        >
          {content.hero_text}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.05 }}
          className="mt-8 flex flex-wrap items-center justify-center gap-4"
        >
          <a
            href="#about"
            onClick={(e) => {
              e.preventDefault();
              scrollToId("#about");
            }}
            data-testid="hero-cta-explore"
            className="group inline-flex items-center gap-2 bg-navy text-paper font-semibold px-8 py-3.5 rounded-full border-2 border-marigold shadow-lg shadow-navy/20 hover:bg-verm hover:border-verm transition-colors duration-300"
          >
            Explore Our Journey
            <Send className="w-4 h-4 group-hover:translate-x-1 group-hover:-translate-y-0.5 transition-transform duration-300" />
          </a>
          <a
            href="#gallery"
            onClick={(e) => {
              e.preventDefault();
              scrollToId("#gallery");
            }}
            data-testid="hero-cta-gallery"
            className="inline-flex items-center gap-2 bg-paper/70 backdrop-blur-sm text-navy font-semibold px-8 py-3.5 rounded-full border-2 border-navy/25 hover:border-navy transition-colors duration-300"
          >
            See The Gallery
          </a>
        </motion.div>
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 pb-8 flex justify-center"
        data-testid="hero-scroll-cue"
      >
        <button
          onClick={() => scrollToId("#about")}
          className="text-navy/70 hover:text-verm transition-colors duration-300"
          aria-label="Scroll to about"
        >
          <ArrowDown className="w-7 h-7 animate-bounce" />
        </button>
      </motion.div>
    </section>
  );
};

export default Hero;
